import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () => /iPhone|iPad|iPod/i.test(window.navigator.userAgent) && !window.MSStream;

export const InstallButton = ({ compact = false }) => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);
  const [hiddenByUser, setHiddenByUser] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }
    if (sessionStorage.getItem("mm_install_dismissed") === "1") {
      setHiddenByUser(true);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome !== "accepted") {
        sessionStorage.setItem("mm_install_dismissed", "1");
        setHiddenByUser(true);
      }
      setDeferred(null);
      return;
    }
    if (isIOS()) {
      setShowIosSheet(true);
    }
  };

  if (installed || hiddenByUser) return null;

  // Show button only when we have a prompt OR we are on iOS (where there is no prompt event)
  const canShow = !!deferred || isIOS();
  if (!canShow) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`btn-trail ${compact ? "px-4 py-2 text-xs" : "text-sm"} bg-mm-steel/15 text-mm-navy hover:bg-mm-steel/25 border-2 border-mm-steel`}
        data-testid="install-app-btn"
        aria-label="Install Mind Map to your home screen"
      >
        <Download size={compact ? 14 : 16} strokeWidth={2.5} />
        {compact ? "Install" : "Pin to home"}
      </button>

      {showIosSheet && (
        <div className="fixed inset-0 z-[60] bg-mm-navy/40 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="install-ios-sheet">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 relative">
            <button
              onClick={() => setShowIosSheet(false)}
              className="absolute top-3 right-3 text-mm-navy/60 hover:text-mm-navy"
              aria-label="Close"
              data-testid="install-ios-close"
            >
              <X size={20} strokeWidth={2.5} />
            </button>
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Pin to home screen</div>
            <h3 className="font-heading text-2xl font-black text-mm-navy mt-2">Install Mind Map on iPhone</h3>
            <ol className="mt-5 space-y-3 text-mm-navy/85 text-[15px]">
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-mm-gold/20 text-mm-navy font-bold flex items-center justify-center shrink-0">1</span>
                <span>Tap the <Share size={16} strokeWidth={2.5} className="inline align-text-bottom mx-1 text-mm-steel" /> <strong>Share</strong> button in Safari's toolbar.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-mm-gold/20 text-mm-navy font-bold flex items-center justify-center shrink-0">2</span>
                <span>Scroll down and tap <strong>Add to Home Screen</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-mm-gold/20 text-mm-navy font-bold flex items-center justify-center shrink-0">3</span>
                <span>Tap <strong>Add</strong> — the compass appears on your home screen.</span>
              </li>
            </ol>
            <button
              onClick={() => setShowIosSheet(false)}
              className="btn-trail btn-gold w-full mt-6"
              data-testid="install-ios-got-it"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallButton;
